import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocalStorage } from "@/hooks";

import type { CarouselAccount, CarouselGroup } from "@/schemas";

type ITimelineContext = {
  pinnedAccount: CarouselAccount | null;
  pinnedGroup: CarouselGroup | null;
  timelineEndpoint?: string;
  timelineId: string;
  setPinnedAccount: React.Dispatch<
    React.SetStateAction<CarouselAccount | null>
  >;
  setPinnedGroup: React.Dispatch<React.SetStateAction<CarouselGroup | null>>;
  setTimelineId: React.Dispatch<React.SetStateAction<string>>;
  resetTimeline(): void;
};

const TimelineContext = createContext<any>({
  pinnedAvatar: null,
  pinnedGroup: null,
  timelineId: "following",
});

interface TimelineProviderProps {
  children: React.ReactNode;
}

const TimelineProvider: React.FC<TimelineProviderProps> = ({ children }) => {
  const [timelineId, setTimelineId] = useLocalStorage<string>(
    "timeline-filter",
    "following",
  );
  const [pinnedAccount, setPinnedAccount] = useState<CarouselAccount | null>(
    null,
  );
  const [pinnedGroup, setPinnedGroup] = useState<CarouselGroup | null>(null);

  const timelineEndpoint = useMemo(() => {
    if (pinnedGroup) {
      return `/api/v1/timelines/group/${pinnedGroup.group_id}`;
    } else if (pinnedAccount) {
      return `/api/v2/truth/carousels/avatars/accounts/${pinnedAccount.account_id}/statuses`;
    }

    return `/api/v1/timelines/${timelineId}`;
  }, [timelineId, pinnedGroup, pinnedAccount]);

  const resetTimeline = () => {
    setTimelineId("following");
    setPinnedAccount(null);
    setPinnedGroup(null);
  };

  const value = useMemo(
    () => ({
      timelineId,
      setTimelineId,
      timelineEndpoint,
      setPinnedAccount,
      pinnedAccount,
      setPinnedGroup,
      pinnedGroup,
      resetTimeline,
    }),
    [timelineId, timelineEndpoint, pinnedAccount, pinnedGroup],
  );

  useEffect(() => {
    setPinnedAccount(null);
    setPinnedGroup(null);
  }, [timelineId]);

  return (
    <TimelineContext.Provider value={value}>
      {children}
    </TimelineContext.Provider>
  );
};

const useTimelineContext = (): ITimelineContext => useContext(TimelineContext);

export { TimelineProvider, useTimelineContext, type ITimelineContext };
